import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  Text,
  Link,
  Box,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast, // Added for temporary signup disabled toast
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getusertoken } from "../../../Redux/Login/action";
import { useVisibilityAwareInterval } from "../../../hooks/useTabVisibility";
import '../../../styles/Signin.css';

const init = {
  burrowerId: "",
  password: "",
};

export const Signin = ({ isOpen, onOpen, onClose, cancelRef, handelhideshow }) => {
  const [userlogin, setuserlogin] = useState(init);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const toast = useToast(); // Toast for messaging when signup is disabled

  // Check if account is locked on component mount
  useEffect(() => {
    const checkLockStatus = () => {
      const lockData = localStorage.getItem("loginLock");
      if (lockData) {
        const { until, attempts } = JSON.parse(lockData);
        // Keep track of attempts but don't actually lock the account
        setLoginAttempts(attempts || 0);
        // Always set isLocked to false to allow login attempts
        setIsLocked(false);
      }
    };

    checkLockStatus();
  }, []);
  
  // Check lock status periodically when tab is visible
  useVisibilityAwareInterval(() => {
    const lockData = localStorage.getItem("loginLock");
    if (lockData) {
      const { until, attempts } = JSON.parse(lockData);
      setLoginAttempts(attempts || 0);
      setIsLocked(false);
    }
  }, 60000, []); // Check every minute when tab is visible

  const handleShowClick = () => setShowPassword(!showPassword);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setuserlogin({ ...userlogin, [name]: value });
  };

  const handelsubmit = (event) => {
    event.preventDefault();

    // Attempt login (removed the isLocked check)
    dispatch(getusertoken({ userlogin, handelhideshow, onClose })).then(
      (result) => {
        if (!result.success) {
          // Login failed, increment attempts
          const newAttempts = loginAttempts + 1;
          setLoginAttempts(newAttempts);

          // Just track attempts, don't lock the account
          localStorage.setItem(
            "loginLock",
            JSON.stringify({
              attempts: newAttempts,
            })
          );
        } else {
          // Login successful, reset attempts
          setLoginAttempts(0);
          localStorage.removeItem("loginLock");
          onClose();
        }
      }
    );
  };

  // Function to format remaining lockout time
  const formatTimeRemaining = () => {
    if (!lockedUntil) return "";

    const now = new Date();
    const diffMs = lockedUntil - now;
    if (diffMs <= 0) return "a moment";

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} and ${minutes} minute${
        minutes > 1 ? "s" : ""
      }`;
    }
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg="white" color="black">
        <ModalHeader textAlign="center">
          <Image
            src="https://bunny-burrow-pull-1.b-cdn.net/Logo-Longlow-White-Back.jpg"
            width="600px"
            maxW="100%"
            mx="auto"
            mb={4}
          />
          <Text>Sign in with your Burrower ID</Text>
          <Text fontSize="sm" color="gray.500">
            You will be signed in to Burrow TV, Tools and Community
          </Text>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4}>
            {!isLocked && loginAttempts > 0 && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Failed login attempts: {loginAttempts}/5
                  {loginAttempts >= 3 && (
                    <Text mt={1}>
                      Incorrect password. Please try again or reset your password.
                    </Text>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <FormControl>
              <FormLabel>Burrower ID</FormLabel>
              <Input
                name="burrowerId"
                onChange={handleChange}
                // Remove the disabled attribute to always allow input
              />
            </FormControl>

            <FormControl>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  onChange={handleChange}
                  // Remove the disabled attribute to always allow input
                />
                <InputRightElement width="3rem" h="100%" display="flex" alignItems="center">
                  <Button
                    h="1.5rem"
                    size="xs"
                    onClick={handleShowClick}
                    // Remove the disabled attribute to always allow toggling password visibility
                  >
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter flexDirection="column" alignItems="center" gap={4}>
          <Button
            colorScheme="blue"
            w="100%"
            onClick={handelsubmit}
            // Remove isDisabled={isLocked} to always allow submission
          >
            Sign In
          </Button>

          <Text
            fontSize="sm"
            color="orange.400"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => {
              onClose();
              navigate('/forgot-password');
            }}
          >
            Forgot Password?
          </Text>

          <Text textAlign="center">
            No Account?{" "}
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              onClick={() => {
                handelhideshow();
              }}
            >
              Become a Burrower Today
            </Text>
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
// https://apple-tv-plus-clone.vercel.app/
// https://github.com/vadimghedreutan/Apple-tv-plus-clone
